import { useEffect, useCallback, useRef } from 'react';
import {
  toolShortcuts,
  editShortcuts,
  viewShortcuts,
  fileShortcuts,
  helpShortcuts,
  type ShortcutDefinition,
} from '@/data/shortcuts';
import type { ManualTool } from '@/types';

export interface HotkeyHandlers {
  // Tool handlers
  onToolChange?: (tool: ManualTool) => void;

  // Edit handlers
  onUndo?: () => void;
  onRedo?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onDeselect?: () => void;
  onSelectAll?: () => void;

  // View handlers
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onResetView?: () => void;
  onToggleGrid?: () => void;
  onFlipSide?: () => void;

  // File handlers
  onSave?: () => void;
  onExport?: () => void;

  // Help handlers
  onShowShortcuts?: () => void;
  onShowDebugPanel?: () => void;
}

export interface UseHotkeysOptions {
  /** Whether hotkeys are enabled */
  enabled?: boolean;
  /** Whether to prevent default browser behavior */
  preventDefault?: boolean;
}

/**
 * Check if the event target is an input element where we should not capture shortcuts
 */
function isInputElement(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;

  const tagName = target.tagName.toLowerCase();
  const isEditable = target.isContentEditable;
  const isInput = tagName === 'input' || tagName === 'textarea' || tagName === 'select';

  return isInput || isEditable;
}

/**
 * Check if a keyboard event matches a shortcut definition
 */
function matchesShortcut(event: KeyboardEvent, shortcut: ShortcutDefinition): boolean {
  const key = event.key.toLowerCase();
  const shortcutKey = shortcut.key.toLowerCase();

  // Check modifiers
  const ctrlMatch = shortcut.ctrl
    ? event.ctrlKey || event.metaKey
    : !event.ctrlKey && !event.metaKey;
  const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;
  const altMatch = shortcut.alt ? event.altKey : !event.altKey;

  // Special handling for ? which requires shift
  if (shortcut.key === '?') {
    return key === '?' || (key === '/' && event.shiftKey);
  }

  // Check key match
  const keyMatch = key === shortcutKey || event.code.toLowerCase() === `key${shortcutKey}`;

  return keyMatch && ctrlMatch && shiftMatch && altMatch;
}

/**
 * Hook for handling keyboard shortcuts throughout the application
 *
 * @example
 * ```tsx
 * useHotkeys({
 *   onToolChange: (tool) => setTool(tool),
 *   onUndo: () => undo(),
 *   onRedo: () => redo(),
 *   onShowShortcuts: () => setShowShortcutsPanel(true),
 * });
 * ```
 */
export function useHotkeys(handlers: HotkeyHandlers, options: UseHotkeysOptions = {}): void {
  const { enabled = true, preventDefault = true } = options;

  // Use refs to avoid stale closures
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Skip if disabled or target is an input
      if (!enabled) return;

      // Allow shortcuts in inputs only for specific keys like Escape
      const isInput = isInputElement(event.target);

      // Check tool shortcuts (only when not in input)
      if (!isInput) {
        for (const shortcut of toolShortcuts) {
          if (matchesShortcut(event, shortcut) && shortcut.tool) {
            if (preventDefault) event.preventDefault();
            handlersRef.current.onToolChange?.(shortcut.tool);
            return;
          }
        }
      }

      // Check edit shortcuts
      for (const shortcut of editShortcuts) {
        if (matchesShortcut(event, shortcut)) {
          // Allow Escape in inputs to deselect/cancel
          if (shortcut.action === 'deselect') {
            if (preventDefault) event.preventDefault();
            handlersRef.current.onDeselect?.();
            return;
          }

          // Skip other edit shortcuts in inputs
          if (isInput) continue;

          if (preventDefault) event.preventDefault();

          switch (shortcut.action) {
            case 'undo':
              handlersRef.current.onUndo?.();
              break;
            case 'redo':
              handlersRef.current.onRedo?.();
              break;
            case 'delete':
              handlersRef.current.onDelete?.();
              break;
            case 'duplicate':
              handlersRef.current.onDuplicate?.();
              break;
            case 'select-all':
              handlersRef.current.onSelectAll?.();
              break;
          }
          return;
        }
      }

      // Check view shortcuts (only when not in input)
      if (!isInput) {
        for (const shortcut of viewShortcuts) {
          if (matchesShortcut(event, shortcut)) {
            if (preventDefault) event.preventDefault();

            switch (shortcut.action) {
              case 'zoom-in':
                handlersRef.current.onZoomIn?.();
                break;
              case 'zoom-out':
                handlersRef.current.onZoomOut?.();
                break;
              case 'reset-view':
                handlersRef.current.onResetView?.();
                break;
              case 'toggle-grid':
                handlersRef.current.onToggleGrid?.();
                break;
              case 'flip-side':
                handlersRef.current.onFlipSide?.();
                break;
            }
            return;
          }
        }
      }

      // Check file shortcuts (only when not in input)
      if (!isInput) {
        for (const shortcut of fileShortcuts) {
          if (matchesShortcut(event, shortcut)) {
            if (preventDefault) event.preventDefault();

            switch (shortcut.action) {
              case 'save':
                handlersRef.current.onSave?.();
                break;
              case 'export':
                handlersRef.current.onExport?.();
                break;
            }
            return;
          }
        }
      }

      // Check help shortcuts (allow in inputs too)
      for (const shortcut of helpShortcuts) {
        if (matchesShortcut(event, shortcut)) {
          // Don't show shortcuts panel when typing ? in an input
          if (isInput && shortcut.action === 'show-shortcuts') continue;

          if (preventDefault) event.preventDefault();

          if (shortcut.action === 'show-shortcuts') {
            handlersRef.current.onShowShortcuts?.();
          } else if (shortcut.action === 'show-debug-panel') {
            handlersRef.current.onShowDebugPanel?.();
          }
          return;
        }
      }
    },
    [enabled, preventDefault]
  );

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled, handleKeyDown]);
}

/**
 * Get all available shortcuts for display
 */
export { allShortcuts, getShortcutsByCategory, categoryLabels } from '@/data/shortcuts';
